import { useState, useEffect } from "react";
import { Row, Col, Tabs, Button, Input, Space, Tooltip } from "antd";
import {
  PlusOutlined,
  ContainerOutlined,
  WalletOutlined,
  PrinterOutlined,
} from "@ant-design/icons";
import { StyleSheet } from "@react-pdf/renderer";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { formatTransactionDateTime } from "../../../utils/sastDate";
import DataTable from "../../DataTable";
import {
  AddFuneralPaymentModal,
  FuneralStatementModal,
  FuneralReceiptModal,
} from "../../ModalForm";
import { getFuneralInvoiceApi } from "../../../services/Funeral/FuneralInvoiceApis";

dayjs.extend(utc);
const { TabPane } = Tabs;
const { Search } = Input;

interface IPaymentProps {
  payments: any;
  invoice: any;
  invoiceId: any;
  handleAdd: Function;
}

const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    alignItems: "center",
    padding: 20,
  },
  section: {
    marginBottom: 10,
  },
  heading: {
    fontSize: 18,
    fontWeight: "bold",
    marginBottom: 10,
  },
  label: {
    fontWeight: "bold",
    marginRight: 5,
  },
  value: {
    marginBottom: 10,
  },
});

const FuneralPaymentTable: React.FC<IPaymentProps> = ({
  payments,
  invoice,
  invoiceId,
  handleAdd,
}) => {
  const [all, setAll] = useState([]); //All Tab
  const [accountBalance, setAccountBalance] = useState(0);
  const [state, setState] = useState({
    payment: false,
    receipt: false,
    statement: false,
    loading: false,
    activeKey: "1",
    pagination: {
      pageSize: 15,
      position: ["bottomRight"],
      total: 0,
      current: 1,
      hideOnSinglePage: true,
    },
    searchItem: "",
  });
  const [receipt, setReceipt] = useState({});

  const showPaymentModal = () => {
    setState((prevState) => ({
      ...prevState,
      payment: !prevState.payment,
    }));
  };

  const showStatementModal = () => {
    setState((prevState) => ({
      ...prevState,
      statement: !prevState.statement,
    }));
  };

  const showReceiptModal = (data: any) => {
    setState((prevState) => ({
      ...prevState,
      receipt: !prevState.receipt,
    }));
    setReceipt({ Payment: data, ...invoice });
  };

  const fetchAll = async (params = {}) => {
    try {
      const all = payments;

      const pagination = state.pagination;
      pagination.total = all.length;

      setState({
        ...state,
        loading: false,
        pagination,
        ...params,
      });

      setAll(all);
    } catch (err) {
      console.log(err);
    }
  };

  const onSearch = (e: any) => {
    const value = e.target.value;
    if (value !== "") {
      const filteredData = all.filter((row: any) => {
        return Object.values(row)
          .join(" ")
          .toLowerCase()
          .includes(value.toLowerCase());
      });
      setAll(filteredData);
    }
  };

  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    const pager = { ...state.pagination };
    pager.current = pagination.current;
    setState({
      ...state,
      pagination: pager,
    });

    const params = {
      pageSize: pagination.pageSize,
      page: pagination.current,
      sortField: sorter.field,
      sortOrder: sorter.order,
      ...filters,
    };

    fetchAll(params);
  };

  useEffect(() => {
    getFuneralInvoiceApi(String(invoiceId))
      .then((res) => {
        setAccountBalance(res.data.OutstandingBalance);
      })
      .catch((err) => {
        console.log(err);
      });

    fetchAll();
  }, [payments]);

  const columns = [
    {
      title: "Payment ID",
      dataIndex: "id",
      render: (id: string) => id.toUpperCase(),
      sorter: (a: any, b: any) => a.Name.localeCompare(b.Name),
    },
    {
      title: "Payment Provider",
      dataIndex: "PaymentProvider",
    },
    {
      title: "Payment Date",
      dataIndex: "Date",
      render: (date: Date) => formatTransactionDateTime(date),
      sorter: (a: any, b: any) =>
        dayjs.utc(a.Date).unix() - dayjs.utc(b.Date).unix(),
    },
    {
      title: "Payment Type",
      dataIndex: "Type",
    },
    {
      title: "Payment Amount",
      dataIndex: "Amount",
    },
    {
      title: "Payment Reference",
      dataIndex: "Reference",
    },
    {
      dataIndex: "Action",
      render: (_: any, record: any) => (
        <Space size="middle">
          <Tooltip title="Print Receipt">
            <PrinterOutlined
              onClick={() => showReceiptModal(record)}
              style={{ color: "green" }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const tabPanes = [
    {
      key: "1",
      title: "All",
      content: (
        <DataTable
          persistKey="funeral-payments"
          dataSource={all}
          columns={columns}
          loading={state.loading}
          pagination={state.pagination}
          onChange={handleTableChange}
        />
      ),
      count: all.length,
    },
  ];

  const changeTab = (key: any) => {
    setState({
      ...state,
      activeKey: key,
      loading: false,
    });
  };

  return (
    <>
      <Row>
        <Col span={24}>
          <div className="interactions">
            <Button
              onClick={showPaymentModal}
              size="large"
              style={{ marginBottom: "12px" }}
            >
              <PlusOutlined style={{ paddingRight: "5px" }} />
              Add Payment
            </Button>

            <div style={{ display: "flex" }}>
              <h3
                style={
                  accountBalance > 0 ? { color: "red" } : { color: "green" }
                }
              >
                <WalletOutlined /> Outstanding Balance: R {accountBalance}
              </h3>
              <Tooltip title="Download Statement">
                <ContainerOutlined
                  onClick={showStatementModal}
                  style={{ marginLeft: "16px" }}
                />
              </Tooltip>
            </div>
            <div>
              <Search
                placeholder="Search"
                size="large"
                onChange={(e) => onSearch(e)}
                style={{ width: "100%", margin: "24px 0" }}
              />
            </div>
          </div>
          <Tabs
            type="card"
            defaultActiveKey={state.activeKey}
            activeKey={state.activeKey}
            onChange={changeTab}
          >
            {tabPanes.map((pane) => (
              <TabPane
                key={pane.key}
                tab={
                  <>
                    <span>{pane.title}</span>
                    <span
                      style={{
                        color: "#B0B0B0",
                        fontWeight: "300",
                        marginLeft: "3px",
                        fontSize: "12px",
                      }}
                    >
                      ({pane.count})
                    </span>
                  </>
                }
              >
                {pane.content}
              </TabPane>
            ))}
          </Tabs>

          {state.payment && (
            <AddFuneralPaymentModal
              show={state.payment}
              invoiceId={invoiceId}
              showModal={showPaymentModal}
              handleAdd={handleAdd}
            />
          )}

          {state.receipt && (
            <FuneralReceiptModal
              show={state.receipt}
              receipt={receipt}
              showModal={showReceiptModal}
              handleAdd={handleAdd}
            />
          )}

          {state.statement && (
            <FuneralStatementModal
              show={state.statement}
              invoice={invoice}
              payments={payments}
              showModal={showStatementModal}
              accountBalance={accountBalance}
            />
          )}
        </Col>
      </Row>
    </>
  );
};

export default FuneralPaymentTable;
